/**
 * The Plan of Record's address — /record/:porId
 *
 *   /record/:porId                    the standing record (latest authoritative version)
 *   /record/:porId/v/:version         one frozen version
 *   /record/:porId/c/:commitmentId    one commitment, at the latest version
 *   /record/:porId/v/:n/c/:cid        one commitment at one version
 *
 * Any of these with `?t=<share token>` opens view-only for anyone who is
 * signed in and holds the link (migration 724: no anonymous read — a signed-out
 * reader is asked to sign in and is returned to the same address, token intact);
 * without a token, for workspace members under RLS. The page is the
 * paper register (spec §4 Move 7): chrome-free, light, frozen, printable.
 *
 * Public prefix: `/record/` is in RequireAuth's PUBLIC_PREFIXES. No app chrome
 * renders here because only Elevation paths render it.
 */

import { useCallback, useMemo, useState } from 'react';
import { Link, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { Check, Link2, Loader2, LogIn, Printer, ShieldOff } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { absoluteUrl, recordAddress, withShareToken, type ShareReadError } from '../../lib/porShareService';
import { useRecordAddress } from './useRecordAddress';
import { CommitmentSheet, RecordSheet } from './RecordSheets';
import './recordPaper.css';

const TOKEN_ERROR_COPY: Record<ShareReadError, { title: string; body: string }> = {
  invalid_token: { title: 'This link is not valid', body: 'The address is right but the key in it is not. Ask whoever sent it for a fresh link.' },
  not_found: { title: 'Nothing at this address', body: 'This link does not point at a record we can find. It may have been mistyped or the record may have been removed.' },
  revoked: { title: 'This link has been withdrawn', body: 'The organization revoked this share link. The record still exists; ask its owner for a new one.' },
  expired: { title: 'This link has expired', body: 'The share link reached its expiry date. Ask its owner for a new one.' },
  out_of_scope: { title: 'This link does not cover that page', body: 'The share link was issued for one version or one commitment. Use the link exactly as it was sent.' },
  no_record: { title: 'No ratified record yet', body: 'This Plan of Record has not been ratified, so there is nothing to show at its address yet.' },
  sign_in_required: { title: 'Sign in to view this record', body: 'This link is valid only for someone signed in to an account. Sign in and you will come straight back to this page.' },
  unavailable: { title: 'The record is temporarily unavailable', body: 'Something went wrong on our side. Try again in a moment.' },
};

function useToken(): string | null {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search).get('t'), [search]);
}

function Sheet({ version, commitmentId }: { version: number | null; commitmentId: string | null }) {
  const { porId = '' } = useParams<{ porId: string }>();
  const token = useToken();
  const state = useRecordAddress({ porId, version, commitmentId, token });
  const [copied, setCopied] = useState(false);

  const address = recordAddress(porId, { version, commitmentId });
  const shareable = withShareToken(address, token);

  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(absoluteUrl(shareable));
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      /* clipboard unavailable — the address bar still has it */
    }
  }, [shareable]);

  return (
    <div className="record-paper">
      <div className="record-paper__toolbar">
        <div className="mx-auto max-w-[46rem] px-4 py-2 flex flex-wrap items-center justify-between gap-2 text-xs" style={{ color: 'var(--paper-ink-3)' }}>
          <span className="truncate font-mono" title={absoluteUrl(address)}>
            {address}
          </span>
          <span className="flex items-center gap-1">
            <Button variant="ghost" size="sm" onClick={() => void copy()} className="!px-2 !py-1 !text-xs" title="Copy this address">
              {copied ? <Check className="h-3.5 w-3.5" /> : <Link2 className="h-3.5 w-3.5" />}
              {copied ? 'Copied' : token ? 'Copy link' : 'Copy address'}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => window.print()} className="!px-2 !py-1 !text-xs" title="Print the sheet">
              <Printer className="h-3.5 w-3.5" /> Print
            </Button>
            {state.access === 'member' && state.status === 'ready' ? (
              <Link to={`/planning/plan-of-record/${porId}/official`} className="px-2 py-1 underline underline-offset-2">
                Open in the instrument
              </Link>
            ) : null}
          </span>
        </div>
      </div>

      {state.status === 'loading' ? (
        <div className="flex items-center justify-center py-32" style={{ color: 'var(--paper-ink-3)' }}>
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : state.status === 'error' ? (
        <ErrorSheet access={state.access} error={state.error} porId={porId} />
      ) : commitmentId ? (
        <CommitmentSheet data={state.data!} token={token} />
      ) : (
        <RecordSheet data={state.data!} token={token} />
      )}
    </div>
  );
}

function ErrorSheet({ access, error, porId }: { access: 'member' | 'token'; error: ShareReadError | string | null; porId: string }) {
  const location = useLocation();
  // Login returns to `from` — pathname AND search, so the ?t= token survives sign-in.
  const returnState = { from: { pathname: location.pathname, search: location.search } };
  const signInRequired = access === 'token' && error === 'sign_in_required';
  const tokenCopy = access === 'token' ? TOKEN_ERROR_COPY[(error as ShareReadError) ?? 'unavailable'] ?? TOKEN_ERROR_COPY.unavailable : null;
  return (
    <article className="record-paper__sheet text-center">
      <ShieldOff className="h-8 w-8 mx-auto" style={{ color: 'var(--paper-ink-3)' }} />
      <h1 className="record-paper__title mt-3" style={{ fontSize: '1.5rem' }}>
        {tokenCopy ? tokenCopy.title : 'Nothing to show at this address yet'}
      </h1>
      <p className="record-paper__body mt-2" style={{ whiteSpace: 'normal' }}>
        {tokenCopy ? tokenCopy.body : (error ?? 'This record could not be loaded.')}
      </p>
      {signInRequired ? (
        <p className="mt-6">
          <Link
            to="/auth/login"
            state={returnState}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium border"
            style={{ borderColor: 'var(--paper-ink)', color: 'var(--paper-ink)' }}
          >
            <LogIn className="h-4 w-4" /> Sign in to view
          </Link>
        </p>
      ) : access === 'member' ? (
        <p className="record-paper__note mt-6">
          <Link to={`/planning/plan-of-record/${porId}/dashboard`} style={{ textDecoration: 'underline' }}>
            Open the Plan of Record dashboard
          </Link>
          {' '}to ratify it — the address answers once a ratified record exists.
        </p>
      ) : (
        <p className="record-paper__note mt-6">
          Workspace members can <Link to="/auth/login" state={returnState} style={{ textDecoration: 'underline' }}>sign in</Link> to read the record directly.
        </p>
      )}
    </article>
  );
}

function VersionSheet() {
  const { version } = useParams<{ version: string }>();
  const n = Number(version);
  return <Sheet version={Number.isInteger(n) && n >= 1 ? n : null} commitmentId={null} />;
}

function CommitmentAtLatest() {
  const { commitmentId = null } = useParams<{ commitmentId: string }>();
  return <Sheet version={null} commitmentId={commitmentId} />;
}

function CommitmentAtVersion() {
  const { version, commitmentId = null } = useParams<{ version: string; commitmentId: string }>();
  const n = Number(version);
  return <Sheet version={Number.isInteger(n) && n >= 1 ? n : null} commitmentId={commitmentId} />;
}

export default function RecordAddressApp() {
  return (
    <Routes>
      <Route index element={<Sheet version={null} commitmentId={null} />} />
      <Route path="v/:version" element={<VersionSheet />} />
      <Route path="c/:commitmentId" element={<CommitmentAtLatest />} />
      <Route path="v/:version/c/:commitmentId" element={<CommitmentAtVersion />} />
      <Route path="*" element={<Sheet version={null} commitmentId={null} />} />
    </Routes>
  );
}
